// ============================================================
// components/stashcard/MapEmbed.tsx
//
// Google Maps Static API embed for the StashCard detail page.
//
// Why Static (not JS):
// - No script load, no JS bundle bloat
// - Single signed URL → a PNG; cacheable at the CDN edge
// - Works behind your existing API proxy if you sign on the server
//
// Wire-up:
// 1. Add `VITE_GOOGLE_MAPS_API_KEY` to your `.env` (read-only browser key,
//    restricted to your hosts). For production, prefer signing the URL on
//    Firebase Functions and returning the signed URL — never expose a
//    key with billing enabled to the client.
// 2. The component falls back to a stylized SVG placeholder when the key
//    is missing, so it never blocks rendering.
// 3. The component picks size based on the host element's pixel width
//    (capped at the API's 640×400 free tier; 2x scale for retina).
//
// Docs: https://developers.google.com/maps/documentation/maps-static
// ============================================================

import { useMemo } from 'react';
import { MapPin } from 'lucide-react';
import { cn } from '@/lib/utils';

interface MapEmbedProps {
  lat?: number;
  lng?: number;
  /** Falls back to address geocoding if lat/lng are missing. */
  address?: string;
  label?: string;
  className?: string;
  /** Height in CSS pixels. Width fills the container. */
  height?: number;
  /** Zoom level (1=world, 20=street). Default 15 is "neighborhood". */
  zoom?: number;
}

/**
 * Build the signed Google Static Maps URL for these props. Returns null when
 * no key is configured — the component renders a placeholder in that case.
 */
function buildMapUrl({ lat, lng, address, zoom = 15 }: MapEmbedProps): string | null {
  // Vite exposes env vars prefixed VITE_ on import.meta.env
  const key = (import.meta as unknown as { env: Record<string, string> }).env?.VITE_GOOGLE_MAPS_API_KEY;
  if (!key) return null;

  // Center: prefer explicit coords; fall back to address.
  const center = lat != null && lng != null
    ? `${lat},${lng}`
    : address ? encodeURIComponent(address) : null;
  if (!center) return null;

  // Marker — same coords as center, branded red.
  const marker = `markers=color:0xFF3B30%7Csize:mid%7C${center}`;

  // Size — capped at Static API free tier; 2x scale for retina.
  const size = '640x320';
  const scale = '2';

  // Styling — subtle, matches the design system's "warm light map" feel.
  // Each `style=...` is a separate query param.
  const styles = [
    'style=feature:poi%7Cvisibility:off',                 // hide POIs
    'style=feature:transit%7Cvisibility:off',             // hide transit
    'style=feature:road%7Celement:labels.icon%7Cvisibility:off',
    'style=feature:landscape%7Celement:geometry%7Ccolor:0xf5f1e8',
    'style=feature:water%7Celement:geometry%7Ccolor:0xc6dff0',
  ].join('&');

  return `https://maps.googleapis.com/maps/api/staticmap`
    + `?center=${center}`
    + `&zoom=${zoom}`
    + `&size=${size}`
    + `&scale=${scale}`
    + `&${marker}`
    + `&${styles}`
    + `&key=${encodeURIComponent(key)}`;
}

/**
 * Build the Google Maps "open in app" URL — used when the user clicks the embed.
 */
function buildDirectionsUrl({ lat, lng, address }: MapEmbedProps): string {
  if (lat != null && lng != null) {
    return `https://www.google.com/maps/search/?api=1&query=${lat},${lng}`;
  }
  if (address) {
    return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`;
  }
  return 'https://www.google.com/maps';
}

export function MapEmbed({
  lat,
  lng,
  address,
  label,
  className,
  height = 200,
  zoom = 15,
}: MapEmbedProps): React.JSX.Element {
  const mapUrl = useMemo(
    () => buildMapUrl({ zoom, ...(lat !== undefined ? { lat } : {}), ...(lng !== undefined ? { lng } : {}), ...(address !== undefined ? { address } : {}) }),
    [lat, lng, address, zoom],
  );
  const openInMapsUrl = useMemo(
    () => buildDirectionsUrl({ ...(lat !== undefined ? { lat } : {}), ...(lng !== undefined ? { lng } : {}), ...(address !== undefined ? { address } : {}) }),
    [lat, lng, address],
  );

  return (
    <a
      href={openInMapsUrl}
      target="_blank"
      rel="noopener noreferrer"
      aria-label={label ? `Open ${label} in Google Maps` : 'Open in Google Maps'}
      className={cn(
        'group/map relative block overflow-hidden border border-border bg-muted',
        'focus-visible:outline-none focus-visible:ring-3 focus-visible:ring-ring/50',
        className,
      )}
      style={{ height }}
    >
      {mapUrl ? (
        <img
          src={mapUrl}
          alt={label ? `Map of ${label}` : 'Map'}
          loading="lazy"
          className="absolute inset-0 h-full w-full object-cover transition-transform duration-500 group-hover/map:scale-[1.02]"
        />
      ) : (
        // Placeholder when no key is configured — same visual identity.
        <PlaceholderMap {...(label !== undefined ? { label } : {})} />
      )}
      {/* Pin overlay (kept on top of either image or placeholder) */}
      <div className="pointer-events-none absolute left-1/2 top-[52%] -translate-x-1/2 -translate-y-full drop-shadow-lg">
        <MapPin className="text-destructive" strokeWidth={2.5} fill="currentColor" size={28} />
      </div>
      {/* "Open in Maps" affordance */}
      <span className="pointer-events-none absolute right-2 bottom-2 rounded-full bg-card/90 px-2.5 py-1 text-[11px] font-semibold text-foreground shadow-sm backdrop-blur opacity-0 transition-opacity group-hover/map:opacity-100">
        Open in Maps ↗
      </span>
    </a>
  );
}

// ───── Placeholder (key missing or offline) ─────

function PlaceholderMap({ label: _label }: { label?: string }) {
  return (
    <svg
      viewBox="0 0 600 240"
      preserveAspectRatio="xMidYMid slice"
      className="absolute inset-0 h-full w-full"
      aria-hidden="true"
    >
      <defs>
        <linearGradient id="mapBgPh" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#F5F1E8" />
          <stop offset="0.7" stopColor="#E8F5E8" />
          <stop offset="1" stopColor="#DCEEE0" />
        </linearGradient>
      </defs>
      <rect width="600" height="240" fill="url(#mapBgPh)" />
      <path d="M-20 80 Q 200 60 320 120 T 620 200" stroke="white" strokeWidth="14" fill="none" opacity="0.95" />
      <path d="M-20 80 Q 200 60 320 120 T 620 200" stroke="#EFC75E" strokeWidth="4" fill="none" />
      <path d="M0 200 Q 150 180 280 220 T 620 240" stroke="white" strokeWidth="8" fill="none" opacity="0.7" />
      <path d="M80 -20 L 200 240" stroke="white" strokeWidth="6" fill="none" opacity="0.6" />
      <path d="M 410 30 Q 460 60 440 100 T 480 140 Q 460 160 500 180 Z" fill="#C6DFF0" opacity="0.85" />
      <ellipse cx="120" cy="160" rx="60" ry="30" fill="#B4D8AC" opacity="0.7" />
    </svg>
  );
}
